import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItemAliasEntry } from "../hooks/useItemAliases";
import { Util } from "../util/Util";

const formatAliasDate = (lastUsed: string | null) =>
	lastUsed ? Util.formatDate(lastUsed, "medium") : "";

type CatalogItem = {
	id: number;
	ItemName?: string;
};

interface Props {
	value: string;
	onChange: (alias: string) => void;
	/** Called when the user finishes (blur / option pick), after resolving catalog match rules. */
	onCommit?: (alias: string) => void;
	itemId: number | null;
	catalogName: string;
	aliases: ItemAliasEntry[];
	loading?: boolean;
	disabled?: boolean;
	catalogItems: CatalogItem[];
	onCatalogMatch?: (itemId: number, itemName: string) => void;
	label?: string;
	size?: "small" | "medium";
	variant?: "filled" | "outlined" | "standard";
	fullWidth?: boolean;
	autoFocus?: boolean;
	inputRef?: React.Ref<HTMLInputElement>;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const PosAliasInput = ({
	value,
	onChange,
	onCommit,
	itemId,
	catalogName,
	aliases,
	loading = false,
	disabled = false,
	catalogItems,
	onCatalogMatch,
	label,
	size = "small",
	variant = "filled",
	fullWidth = true,
	autoFocus = false,
	inputRef,
	onFocus,
}: Props) => {
	const { t } = useTranslation();

	const options = useMemo(() => {
		const seen = new Set<string>();
		const merged: ItemAliasEntry[] = [];

		const pushAlias = (alias: string, entry?: Partial<ItemAliasEntry>) => {
			const key = alias.trim().toLowerCase();
			if (!key || seen.has(key)) return;
			seen.add(key);
			merged.push({
				alias,
				last_used: entry?.last_used ?? null,
				is_last_used: entry?.is_last_used ?? false,
			});
		};

		for (const entry of aliases) {
			pushAlias(entry.alias, entry);
		}

		if (catalogName) {
			pushAlias(catalogName, { is_last_used: Util.compareStrings(value, catalogName) });
		}

		return merged;
	}, [aliases, catalogName, value]);

	const commitAlias = (nextValue: string) => {
		const trimmed = nextValue.trim();
		if (!trimmed) {
			const fallback = catalogName || value;
			onChange(fallback);
			onCommit?.(fallback);
			return;
		}

		const catalogMatch = catalogItems.find((item) =>
			Util.compareStrings(item.ItemName || "", trimmed)
		);

		if (catalogMatch && onCatalogMatch && catalogMatch.id !== itemId) {
			onCatalogMatch(catalogMatch.id, catalogMatch.ItemName || trimmed);
			return;
		}

		onChange(trimmed);
		onCommit?.(trimmed);
	};

	return (
		<Autocomplete
			freeSolo
			size={size}
			fullWidth={fullWidth}
			disabled={disabled || !itemId}
			loading={loading}
			options={options}
			value={value}
			inputValue={value}
			getOptionLabel={(option) => (typeof option === "string" ? option : option.alias)}
			isOptionEqualToValue={(option, selected) =>
				Util.compareStrings(
					typeof option === "string" ? option : option.alias,
					typeof selected === "string" ? selected : selected.alias,
				)
			}
			onInputChange={(_, newInputValue, reason) => {
				if (reason === "input" || reason === "clear") {
					onChange(newInputValue);
				}
			}}
			onChange={(_, newValue) => {
				if (typeof newValue === "string") {
					commitAlias(newValue);
					return;
				}
				if (newValue?.alias) {
					commitAlias(newValue.alias);
				}
			}}
			onBlur={() => commitAlias(value)}
			renderOption={(props, option) => {
				const entry = typeof option === "string"
					? { alias: option, is_last_used: false, last_used: null }
					: option;
				const { key, ...optionProps } = props;

				return (
					<Box
						component="li"
						key={key}
						{...optionProps}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
							width: "100%",
							...(entry.is_last_used && {
								bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
								borderLeft: 3,
								borderColor: "success.main",
								pl: 1.5,
								"&.Mui-focused": {
									bgcolor: (theme) => alpha(theme.palette.success.main, 0.22),
								},
								"&.Mui-focusVisible": {
									bgcolor: (theme) => alpha(theme.palette.success.main, 0.22),
								},
							}),
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
							<Typography
								variant="body2"
								noWrap
								sx={{ fontWeight: entry.is_last_used ? 700 : 400 }}
							>
								{entry.alias}
							</Typography>
							{entry.is_last_used && (
								<Chip
									label={t("common:ui.labels.lastUsedAlias")}
									size="small"
									color="success"
									sx={{ fontWeight: 700, flexShrink: 0 }}
								/>
							)}
						</Box>
						{!entry.is_last_used && entry.last_used && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ flexShrink: 0 }}
							>
								{formatAliasDate(entry.last_used)}
							</Typography>
						)}
					</Box>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label ?? t("common:ui.labels.receiptName")}
					variant={variant}
					inputRef={inputRef}
					autoFocus={autoFocus}
					onFocus={onFocus}
				/>
			)}
		/>
	);
};

export default PosAliasInput;